import React, { useEffect, useRef, useState } from 'react';

import { Box, IconButton, Typography } from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MicOffIcon from '@mui/icons-material/MicOff';
import BrushIcon from '@mui/icons-material/Brush';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as DroneMode } from '@public/images/DroneMode.svg';
import { ReactComponent as LevelEditor } from '@public/images/LevelEditor.svg';
import IconPopover from '../../../IconPopover';
import ToolBoxItem from '../../../ToolBoxItem';
import {
  BrandingListModal,
  CreateBrandingModal,
  EditAreaPickerModal,
  NameBrandingModal,
  SelectBrandingModal,
  IBranding,
  mapBrandingFromApi,
} from '../../../BrandingModeModals';
import type { EditorFocus } from '../../../EditorMode/editorCategories';
import { useBrandingEntityCatalogue } from '../../../EditorMode/useBrandingEntityCatalogue';
import { useBrandingSwitch } from '../../../EditorMode/useBrandingSwitch';
import { useBrandingEditorLock } from '../../../BrandingEditorLock';

import { useAdminTools } from './hooks';

import { useStyles } from './styles';
import { useMytaverseEvent } from '../../../../providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import EventsService from '@services/EventsService';
import BrandingsService, { IBrandingApi } from '@services/BrandingsService';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import OVERLAYS from '@constants/overlays';
import { env } from 'env';

const { REACT_APP_SHOW_MUTE_ALL_IN_CONFERENCE } = env;

/**
 * Re-reads the Editor Mode lock each time the popover opens.
 *
 * The popover unmounts its children when closed, so mounting is opening. The
 * broadcast keeps the state current while the socket is up; this catches a
 * release it missed, right when the admin is looking at the button.
 *
 * @param props.refresh - Stable callback that re-reads the lock.
 */
const EditorLockRefresher = ({ refresh }: { refresh: () => void }) => {
  useEffect(() => {
    refresh();
  }, [refresh]);

  return null;
};

const AdminTools = () => {
  const { translate, handleDroneMode, handleMuteAll, handleLevelEditor } =
    useAdminTools();

  const {
    currentEvent,
    currentRoom,
    currentParticipant,
    setCurrentRoom,
    setRoomSpots,
    setSelectedCustomizationUeId,
    isBrandingModeEnabled,
    setIsBrandingModeEnabled,
    currentBrandingId,
    setCurrentBrandingId,
    brandingActionRequest,
    setBrandingActionRequest,
    setBrandingEditorFocus,
  } = useMytaverseEvent();
  const { isAvailable: isBrandingEntityEditorAvailable } =
    useBrandingEntityCatalogue();
  const {
    isLockedByOther: isEditorLockedByOther,
    holderName: editorLockHolderName,
    acquire: acquireEditorLock,
    refresh: refreshEditorLock,
  } = useBrandingEditorLock();

  const classes = useStyles(isBrandingModeEnabled);
  const { sendMessageToUnreal } = useMytaverse();
  const { showNotification } = useNotification();

  const [showBrandingListModal, setShowBrandingListModal] = useState(false);
  const [showCreateBrandingModal, setShowCreateBrandingModal] = useState(false);
  const [showNameBrandingModal, setShowNameBrandingModal] = useState(false);
  const [showSelectBrandingModal, setShowSelectBrandingModal] = useState(false);
  const [isCloneMode, setIsCloneMode] = useState(false);
  const [selectedCloneBrandingId, setSelectedCloneBrandingId] = useState<
    string | null
  >(null);

  const [editAreaPickerBrandingId, setEditAreaPickerBrandingId] = useState<
    string | null
  >(null);

  const [brandings, setBrandings] = useState<IBranding[]>([]);
  const [brandingsLoading, setBrandingsLoading] = useState(false);
  // Raw API rows kept alongside the mapped ones: an editor-mode switch needs
  // each configuration's entity overrides, which the list shape drops.
  const brandingsApiRef = useRef<IBrandingApi[]>([]);

  const { switchBranding } = useBrandingSwitch({
    onSwitched: () => setShowBrandingListModal(false),
  });

  const mapId = currentRoom?.map?.mapId;

  const isBrandingFlowOpen =
    showBrandingListModal ||
    showCreateBrandingModal ||
    showSelectBrandingModal ||
    showNameBrandingModal ||
    editAreaPickerBrandingId !== null;

  useEffect(() => {
    if (!isBrandingFlowOpen) {
      setBrandings([]);
      return;
    }
    if (!mapId || !currentRoom?.roomId) {
      setBrandings([]);
      setBrandingsLoading(false);
      return;
    }
    setBrandingsLoading(true);

    const loadBrandings = async () => {
      try {
        const room = await EventsService.getRoom(currentRoom.roomId);
        if (room?.activeBrandingId) {
          setCurrentRoom((prev) =>
            prev && prev.roomId === room.roomId
              ? { ...prev, activeBrandingId: room.activeBrandingId }
              : prev,
          );
        }

        const res = await BrandingsService.getBrandings({ mapId, limit: 100 });
        brandingsApiRef.current = res.brandings ?? [];
        const activeBrandingId =
          room?.activeBrandingId ?? currentRoom?.activeBrandingId ?? null;
        const mapped = (res.brandings ?? []).map((b, i) =>
          mapBrandingFromApi(b, i, activeBrandingId),
        );
        setBrandings(mapped);

        // Only outside the editor: in it `currentBrandingId` names the
        // configuration being authored, which is not necessarily the room's
        // live one. A reload — and a remote activation now triggers one through
        // the `activeBrandingId` dependency — would otherwise move the author
        // onto somebody else's choice mid-edit.
        if (!isBrandingModeEnabled) {
          const active = mapped.find((b) => b.isActive) || mapped[0];
          setCurrentBrandingId(active ? active.id : null);
        }
      } catch {
        setBrandings([]);
      } finally {
        setBrandingsLoading(false);
      }
    };

    loadBrandings();
  }, [
    currentRoom?.activeBrandingId,
    currentRoom?.roomId,
    isBrandingFlowOpen,
    isBrandingModeEnabled,
    mapId,
    setCurrentBrandingId,
    setCurrentRoom,
  ]);

  const isLevelEditingEnabled =
    currentEvent?.world?.isLevelEditingEnabled ?? false;

  const handleOpenBrandingMode = () => {
    setShowBrandingListModal(true);
  };

  const handleOpenCreateBrandingModal = () => {
    setShowBrandingListModal(false);
    setShowCreateBrandingModal(true);
  };

  useEffect(() => {
    if (brandingActionRequest === 'library') {
      setShowBrandingListModal(true);
      setBrandingActionRequest(null);
    } else if (brandingActionRequest === 'create') {
      handleOpenCreateBrandingModal();
      setBrandingActionRequest(null);
    }
  }, [brandingActionRequest, setBrandingActionRequest]);

  const handleCloseCreateBrandingModal = () => {
    setShowCreateBrandingModal(false);
  };

  const handleCloseBrandingListModal = () => {
    setShowBrandingListModal(false);
  };

  const handleToggleBranding = async (id: string) => {
    // Closed up front, like the Edit button: activation continues in the
    // background (two requests plus the spot fetches in editor mode) and the
    // dialog has nothing left to show. Waiting for the switch to resolve left
    // it sitting open over the map for the whole sequence, and on failure it
    // never closed at all. The optimistic state updates below, and
    // `switchBranding`'s own rollback, are what surface the outcome.
    setShowBrandingListModal(false);

    // Outside the editor the backend's nudge is enough: UE re-reads its own
    // state. Inside it the hub owns the entity overrides, so the switch has to
    // run the full editor sequence or UE keeps applying the old ones.
    if (isBrandingModeEnabled) {
      await switchBranding(id, brandingsApiRef.current);
      return;
    }

    let previousBrandings: IBranding[] = [];

    setBrandings((prev) => {
      previousBrandings = prev;
      return prev.map((branding) => ({
        ...branding,
        isActive: branding.id === id,
      }));
    });
    setCurrentBrandingId(id);

    const roomId = currentRoom?.roomId;

    if (!roomId) {
      return;
    }

    try {
      await BrandingsService.activateBranding(id, roomId);

      setCurrentRoom((prev) =>
        prev && prev.roomId === roomId
          ? { ...prev, activeBrandingId: id }
          : prev,
      );
    } catch {
      setBrandings(previousBrandings);
      const active =
        previousBrandings.find((b) => b.isActive) || previousBrandings[0];
      setCurrentBrandingId(active ? active.id : null);
    }
  };

  /**
   * Opens the editor for this participant.
   *
   * Takes the experience's Editor Mode lock first: somebody else editing is
   * answered with a notification and nothing else changes, so a caller that
   * has more to do afterwards (activate, fetch) must stop on `false`.
   *
   * @param options.lockAcquired - The caller already took the lock in this flow.
   * @returns Whether the editor was opened.
   */
  const enterBrandingMode = async (
    options: { lockAcquired?: boolean } = {},
  ): Promise<boolean> => {
    if (!options.lockAcquired && !(await acquireEditorLock())) {
      return false;
    }

    setIsBrandingModeEnabled(true);
    if (!isCloneMode) {
      sendMessageToUnreal({
        action: 'CLEAR_SPOTS',
      });
    }

    sendMessageToUnreal({
      action: 'ENTER_TO_CUSTOMIZATION_MODE',
    });

    if (currentRoom) {
      const roomSpots = await EventsService.getRoomSpots(currentRoom.roomId);
      setRoomSpots(roomSpots);
      setSelectedCustomizationUeId(null);
    }

    return true;
  };

  const handleEditBranding = (id: string) => {
    setShowBrandingListModal(false);
    setEditAreaPickerBrandingId(id);
  };

  const handleCloseEditAreaPicker = () => {
    setEditAreaPickerBrandingId(null);
  };

  const handleBackFromEditAreaPicker = () => {
    setEditAreaPickerBrandingId(null);
    setShowBrandingListModal(true);
  };

  const handlePickEditArea = async (focus: EditorFocus) => {
    const id = editAreaPickerBrandingId;
    if (!id) {
      return;
    }
    setEditAreaPickerBrandingId(null);
    setBrandingEditorFocus(focus);

    if (!(await enterBrandingMode())) {
      return;
    }

    await handleToggleBranding(id);
  };

  const handleCloneFromBranding = (id: string) => {
    setSelectedCloneBrandingId(id);
    setIsCloneMode(true);
    setShowBrandingListModal(false);
    setShowNameBrandingModal(true);
  };

  const handleDeleteBranding = async (id: string) => {
    const isActiveBranding =
      currentBrandingId === id ||
      brandings.some((b) => b.id === id && b.isActive);
    if (isActiveBranding) {
      return;
    }

    let previousBrandings: IBranding[] = [];
    let deletedInState = false;
    let nextActiveBrandingId: string | null = currentBrandingId;

    setBrandings((prev) => {
      previousBrandings = prev;

      const filtered = prev.filter((b) => b.id !== id);
      if (filtered.length === 0) {
        return prev;
      }

      deletedInState = true;

      const hasActive = filtered.some((b) => b.isActive);
      if (hasActive) {
        const active = filtered.find((b) => b.isActive);
        nextActiveBrandingId = active ? active.id : null;
        return filtered;
      }

      const withActive = filtered.map((b, idx) => ({
        ...b,
        isActive: idx === 0,
      }));
      nextActiveBrandingId = withActive.length ? withActive[0].id : null;
      return withActive;
    });

    if (!deletedInState) {
      return;
    }

    setCurrentBrandingId(nextActiveBrandingId || null);

    try {
      await BrandingsService.deleteBranding(id);
    } catch {
      setBrandings(previousBrandings);
    }
  };

  const handleStartFromScratch = () => {
    setShowCreateBrandingModal(false);
    setIsCloneMode(false);
    setShowNameBrandingModal(true);
  };

  const handleCloneFromExisting = () => {
    setShowCreateBrandingModal(false);
    setShowSelectBrandingModal(true);
  };

  const handleSelectBranding = (brandingId: string) => {
    setSelectedCloneBrandingId(brandingId);
    setShowSelectBrandingModal(false);
    setIsCloneMode(true);
    setShowNameBrandingModal(true);
  };

  const handleCloseSelectBrandingModal = () => {
    setShowSelectBrandingModal(false);
  };

  const handleBackFromNameModal = () => {
    setShowNameBrandingModal(false);
    if (isCloneMode) {
      setShowSelectBrandingModal(true);
    } else {
      setShowCreateBrandingModal(true);
    }
  };

  const handleCloseNameModal = () => {
    setShowNameBrandingModal(false);
    setIsCloneMode(false);
    setSelectedCloneBrandingId(null);
  };

  const handleContinueWithName = async (name: string) => {
    setShowNameBrandingModal(false);

    // Before the create request, not after it: a refusal must not leave a
    // freshly created — and activated — configuration that nobody is editing.
    if (!(await acquireEditorLock())) {
      setIsCloneMode(false);
      setSelectedCloneBrandingId(null);
      return;
    }

    const mapId = currentRoom?.map?.mapId;
    const authorEmail = currentParticipant?.email;

    if (!mapId) {
      await enterBrandingMode({ lockAcquired: true });
      setIsCloneMode(false);
      setSelectedCloneBrandingId(null);
      return;
    }

    try {
      const created = await BrandingsService.createBranding({
        mapId,
        name,
        ...(isCloneMode && selectedCloneBrandingId
          ? { parentBrandingId: selectedCloneBrandingId }
          : {}),
        ...(authorEmail ? { author: authorEmail } : {}),
      });
      const mapped = mapBrandingFromApi(created, 0);
      setCurrentBrandingId(mapped.id);

      const roomId = currentRoom?.roomId;
      // What the room actually adopted, which is only the new configuration once the activation
      // below has landed. Creating and activating are two calls and the second can fail on its own.
      let activeBrandingId = currentRoom?.activeBrandingId ?? null;

      if (roomId) {
        try {
          await BrandingsService.activateBranding(mapped.id, roomId);
          activeBrandingId = mapped.id;
          setCurrentRoom((prev) =>
            prev && prev.roomId === roomId
              ? { ...prev, activeBrandingId: mapped.id }
              : prev,
          );
        } catch (activateError) {
          // Not swallowed, because this failure is invisible otherwise and its consequences are
          // not. The editor opens on the new configuration either way — the author made it and is
          // about to edit it, so taking it away would be worse — but the room still names the old
          // one, so the map-editor bridge has already republished the new (empty) layout to
          // everyone while the next participant to arrive will republish the old one over the top.
          // The author is the only one who can resolve that, by activating it again from the
          // switcher, so they have to be told rather than left to discover it.
          console.error(
            `Failed to activate the configuration just created: ${
              (activateError as Error)?.message
            }`,
            { brandingId: mapped.id, roomId, mapId },
          );

          showNotification({
            message: translate(
              'editorMode.activateCreatedBrandingFailed',
              'The configuration was created but could not be made live for this room. Open the configuration switcher and select it again.',
            ),
            type: NOTIFICATION_TYPES.ERROR,
          });
        }
      }

      const res = await BrandingsService.getBrandings({ mapId, limit: 100 });
      // Marks the row the room adopted, not the one just created — they are the same only when the
      // activation succeeded, and a list claiming otherwise is the author's only view of which
      // configuration everyone else is on.
      const refreshed = (res.brandings ?? []).map((b, i) =>
        mapBrandingFromApi(b, i, activeBrandingId),
      );
      setBrandings(refreshed);
    } catch (createError) {
      console.error(
        `Failed to create a configuration: ${(createError as Error)?.message}`,
        { mapId, parentBrandingId: selectedCloneBrandingId },
      );
    }

    await enterBrandingMode({ lockAcquired: true });
    setIsCloneMode(false);
    setSelectedCloneBrandingId(null);
  };

  const handleLeaveBrandingMode = () => {
    setShowBrandingListModal(false);
    setRoomSpots([]);
    setIsBrandingModeEnabled(false);
    setCurrentBrandingId(null);
    // The focus outlives the editor overlay, and the entry paths that do not
    // pick a section (the Edit button's toggle, and creating a configuration by
    // name) would otherwise reopen on whatever tab the last session ended on.
    setBrandingEditorFocus('environment');
    sendMessageToUnreal({
      action: 'LEAVE_CUSTOMIZATION_MODE',
    });
  };

  const brandingModeIcon = <BrushIcon sx={classes.brandingModeIcon} />;

  return (
    <>
      <IconPopover
        icon={<ManageAccountsIcon sx={classes.icon} />}
        label={translate('adminToolbox.toolboxLabel')}
        position={{ vertical: 'top', horizontal: 'right' }}
        analyticsName="admin"
        overlayName={OVERLAYS.ADMIN_TOOLS}
        dataTestId="admin-tools-launcher-btn"
      >
        {({ onClose }) => (
          <Box component="div" sx={classes.container}>
            <EditorLockRefresher refresh={refreshEditorLock} />
            <Box component="div" sx={classes.header}>
              <Typography sx={classes.title}>
                {translate('adminToolbox.title', 'Admin Tools')}
              </Typography>
              <IconButton
                onClick={onClose}
                sx={classes.closeButton}
                data-testid="admin-tools-popover-close-btn"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box component="div" sx={classes.tools}>
              <ToolBoxItem
                Icon={brandingModeIcon}
                title={translate('adminToolbox.editorMode', 'Editor Mode')}
                // One editor per experience: while somebody else holds the
                // lock the item is greyed out and names them. The library is
                // unreachable then too, on purpose — its Edit and Create both
                // end in the editor.
                subtitle={
                  isEditorLockedByOther
                    ? translate(
                        'adminToolbox.editorModeLockedBy',
                        'Editing now: {{name}}',
                        { name: editorLockHolderName },
                      )
                    : undefined
                }
                disabled={isEditorLockedByOther}
                hint={translate(
                  'adminToolbox.editorModeSingleEditorHint',
                  'Only one user can be in Editor Mode at a time.',
                )}
                // Closes the popover as well, like Level Editor: this opens the
                // configuration library over the world, and leaving the popover
                // behind it stacked two overlays whose close buttons sit in the
                // same corner.
                handleClick={() => {
                  handleOpenBrandingMode();
                  onClose();
                }}
                dataTestId="admin-tools-editor-mode-btn"
              />
              <ToolBoxItem
                Icon={<DroneMode />}
                title={translate('adminToolbox.droneMode')}
                handleClick={handleDroneMode}
                dataTestId="admin-tools-drone-mode-btn"
              />
              {REACT_APP_SHOW_MUTE_ALL_IN_CONFERENCE === 'true' ? (
                <ToolBoxItem
                  Icon={<MicOffIcon />}
                  title={translate('adminToolbox.muteAll')}
                  handleClick={handleMuteAll}
                  dataTestId="admin-tools-mute-all-btn"
                />
              ) : null}
              {isLevelEditingEnabled ? (
                <ToolBoxItem
                  Icon={<LevelEditor />}
                  title={translate('adminToolbox.levelEditor')}
                  handleClick={() => {
                    handleLevelEditor();
                    onClose();
                  }}
                  dataTestId="admin-tools-level-editor-btn"
                />
              ) : null}
            </Box>
          </Box>
        )}
      </IconPopover>

      <BrandingListModal
        open={showBrandingListModal}
        onClose={handleCloseBrandingListModal}
        onLeaveBrandingMode={handleLeaveBrandingMode}
        onCreateNew={handleOpenCreateBrandingModal}
        brandings={brandings}
        brandingsLoading={brandingsLoading}
        onToggleBranding={handleToggleBranding}
        onEditBranding={handleEditBranding}
        onCloneBranding={handleCloneFromBranding}
        onDeleteBranding={handleDeleteBranding}
        liveBrandingId={currentRoom?.activeBrandingId ?? null}
        eventName={currentEvent?.name}
      />

      <CreateBrandingModal
        open={showCreateBrandingModal}
        onClose={handleCloseCreateBrandingModal}
        onStartFromScratch={handleStartFromScratch}
        onCloneFromExisting={handleCloneFromExisting}
        hasExistingBrandings={brandings.length > 0}
      />

      <NameBrandingModal
        open={showNameBrandingModal}
        onClose={handleCloseNameModal}
        onBack={handleBackFromNameModal}
        onContinue={handleContinueWithName}
      />

      <SelectBrandingModal
        open={showSelectBrandingModal}
        onClose={handleCloseSelectBrandingModal}
        onSelect={handleSelectBranding}
        brandings={brandings}
      />

      <EditAreaPickerModal
        open={editAreaPickerBrandingId !== null}
        brandingName={
          brandings.find((b) => b.id === editAreaPickerBrandingId)?.name
        }
        isObjectsAvailable={isBrandingEntityEditorAvailable}
        onBack={handleBackFromEditAreaPicker}
        onClose={handleCloseEditAreaPicker}
        onPick={handlePickEditArea}
      />
    </>
  );
};

export default AdminTools;
